import {BrowserModule} from '@angular/platform-browser';
import {NgModule, APP_INITIALIZER, APP_ID} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {HttpClientModule, HttpClient, HTTP_INTERCEPTORS} from '@angular/common/http';
import {Router, RouterModule} from '@angular/router';

import {AuthGuard} from './injectables/auth.guard';
import {AppComponent} from './app.component';

import {HomeComponent} from './pages/home/home.component';
import {EmployeeOverviewComponent} from "./pages/employees/overview/employee-overview.component";
import {LoginComponent} from "./pages/login/login.component";
import {NotFoundComponent} from "./pages/not-found/not-found.component";

import {NavMenuComponent} from './components/nav-menu/nav-menu.component';
import {SessionMenuComponent} from "./components/session-menu/session-menu.component";
import {DataTableComponent} from "./components/data-table/data-table.component";

import {UserService} from "./services/user.service";
import {IsFilterablePipe} from "./components/data-table/filters/filterable.pipe";
import {DataTableFilterComponent} from "./components/data-table/filters/data-table-filter.component";
import {NumberFilterComponent} from "./components/data-table/filters/number-filter/number-filter.component";
import {FilterWrapperComponent} from "./components/data-table/filters/filter-wrapper/filter-wrapper.component";
import {StringFilterComponent} from "./components/data-table/filters/string-filter/string-filter.component";
import {FilterComponent} from "./components/data-table/filters/filter/filter.component";
import {DateFilterComponent} from "./components/data-table/filters/date-filter/date-filter.component";
import {ListFilterComponent} from "./components/data-table/filters/list-filter/list-filter.component";
import {ErrorToastComponent} from "./components/error-toast/error-toast.component";
import {ToastService} from "./services/toast.service";
import {CalendarModule, DateAdapter} from "angular-calendar";
import {adapterFactory} from "angular-calendar/date-adapters/date-fns";
import {BrowserAnimationsModule} from "@angular/platform-browser/animations";
import {DataTableCellDirective} from "./components/data-table/cell/data-table-cell.component";
import {ColorIndicatorComponent} from "./components/color-indicator/color-indicator.component";
import {DataTablePagerComponent} from "./components/data-table/pager/data-table-pager.component";
import {FileAttachmentComponent} from "./components/file-attachments/file-attachment/file-attachment.component";
import {
	FileAttachmentInputComponent
} from "./components/file-attachments/file-attachment-input/file-attachment-input.component";
import {
	FileAttachmentDisplayComponent
} from "./components/file-attachments/file-attachment-display/file-attachment-display.component";
import {
	FileAttachmentListComponent
} from "./components/file-attachments/file-attachment-list/file-attachment-list.component";
import {TitleService} from "./services/title.service";
import {EnvironmentService} from "./services/environment.service";
import {UtilityService} from "./services/utils.service";
import {FileDownloadHttpInterceptor} from "./injectables/file-download-http-interceptor";
import {BooleanFilterComponent} from "./components/data-table/filters/boolean-filter/boolean-filter.component";
import {BASE_PATH} from "./apis/backend";
import {EmployeeDialogComponent} from "./pages/employees/dialog/employee-dialog.component";
import {EmployeeDetailComponent} from "./pages/employees/detail/employee-detail.component";
import {AutofocusDirective} from "./directives/autofocus.directive";
import {NgbModule} from "@ng-bootstrap/ng-bootstrap";
import {MessageComponent} from "./pages/messages/message.component";
import {MessageDialogComponent} from "./pages/messages/dialog/message-dialog.component";
import {TaskOverviewComponent} from "./pages/tasks/overview/task-overview.component";
import {TaskDetailComponent} from "./pages/tasks/detail/task-detail.component";
import {TaskDialogComponent} from "./pages/tasks/dialog/task-dialog.component";
import {TaskTypeDialogComponent} from "./pages/tasks/type-dialog/task-type-dialog.component";
import {DepartmentDialogComponent} from "./pages/employees/department-dialog/department-dialog.component";
import {CredentialsDialogComponent} from "./pages/employees/credentials-dialog/credentials-dialog.component";
import {BarrelOverviewComponent} from "./pages/barrels/overview/barrel-overview.component";
import {ProductAmountOverviewComponent} from "./pages/product-amounts/overview/product-amount-overview.component";
import {BarrelDialogComponent} from "./pages/barrels/dialog/barrel-dialog.component";
import {BarrelDetailComponent} from "./pages/barrels/detail/barrel-detail.component";
import {ProductAmountDetailComponent} from "./pages/product-amounts/detail/product-amount-detail.component";
import {ProductAmountDialogComponent} from "./pages/product-amounts/dialog/product-amount-dialog.component";
import {MarketingFilesComponent} from "./pages/marketing-files/marketing-files.component";

@NgModule({
	declarations: [
		AppComponent,
		
		// Pages
		HomeComponent,
		LoginComponent,
		BarrelOverviewComponent,
		BarrelDetailComponent,
		EmployeeOverviewComponent,
		EmployeeDetailComponent,
		MarketingFilesComponent,
		MessageComponent,
		ProductAmountOverviewComponent,
		ProductAmountDetailComponent,
		TaskOverviewComponent,
		TaskDetailComponent,
		NotFoundComponent,
		
		// Components
		
		// Dialogs
		BarrelDialogComponent,
		CredentialsDialogComponent,
		DepartmentDialogComponent,
		EmployeeDialogComponent,
		MessageDialogComponent,
		ProductAmountDialogComponent,
		TaskDialogComponent,
		TaskTypeDialogComponent,
		
		// Data Table Components
		DataTableComponent,
		DataTableCellDirective,
		DataTableFilterComponent,
		DataTablePagerComponent,
		FilterComponent,
		FilterWrapperComponent,
		BooleanFilterComponent,
		DateFilterComponent,
		ListFilterComponent,
		NumberFilterComponent,
		StringFilterComponent,

		// File Attachment Components
		FileAttachmentComponent,
		FileAttachmentListComponent,
		FileAttachmentDisplayComponent,
		FileAttachmentInputComponent,
		
		// Other Components
		AutofocusDirective,
		ColorIndicatorComponent,
		ErrorToastComponent,
		NavMenuComponent,
		SessionMenuComponent,
		
		IsFilterablePipe
	],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
		CalendarModule.forRoot({
			provide: DateAdapter,
			useFactory: adapterFactory
		}),
		FormsModule,
        HttpClientModule,
		NgbModule,
        RouterModule.forRoot([
			{path: '', component: HomeComponent, pathMatch: 'full', canActivate: [AuthGuard]},
            {path: 'login', component: LoginComponent},
			{path: 'barrels', pathMatch: 'full', component: BarrelOverviewComponent, canActivate: [AuthGuard]},
			{path: 'barrels/:barrelID', pathMatch: 'full', component: BarrelDetailComponent, canActivate: [AuthGuard]},
			{path: 'tasks', pathMatch: 'full', component: TaskOverviewComponent, canActivate: [AuthGuard]},
			{path: 'tasks/:taskID', pathMatch: 'full', component: TaskDetailComponent, canActivate: [AuthGuard]},
			{path: 'employees', pathMatch: 'full', component: EmployeeOverviewComponent, canActivate: [AuthGuard]},
			{path: 'employees/:employeeID', component: EmployeeDetailComponent, canActivate: [AuthGuard]},
			{path: 'marketing-files', pathMatch: 'full', component: MarketingFilesComponent, canActivate: [AuthGuard]},
			{path: 'messages', pathMatch: 'full', component: MessageComponent, canActivate: [AuthGuard]},
			{path: 'product-amounts', pathMatch: 'full', component: ProductAmountOverviewComponent, canActivate: [AuthGuard]},
			{path: 'product-amounts/:productAmountID', pathMatch: 'full', component: ProductAmountDetailComponent, canActivate: [AuthGuard]},
            {path: '**', component: NotFoundComponent, pathMatch: 'full'}
        ]),
    ],
	providers: [
		{ provide: APP_ID, useValue: 'ng-cli-universal' },
		{ provide: BASE_PATH, useFactory: getApiBasePath },
		{ provide: HTTP_INTERCEPTORS, useClass: FileDownloadHttpInterceptor, multi: true },
		EnvironmentService,
		TitleService,
		ToastService,
		UserService,
		UtilityService,
		AuthGuard
	],
	bootstrap: [AppComponent]
})
export class AppModule {
}

function getApiBasePath() {
	return window.location.origin;
}